import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { IntentRing } from "@/components/IntentRing";
import {
  ArrowRight, Languages, ShieldCheck, BookOpenCheck, FileDown,
  Sparkles, Globe, ScanSearch, Layers,
} from "lucide-react";

const FEATURES = [
  { icon: Languages, title: "RAVIRA engine", body: "Chapter-by-chapter translation across 106 languages, preserving structure, headings and formatting.", tag: "Stage 1" },
  { icon: ScanSearch, title: "Intent corrector", body: "Your chosen AI model compares every passage to the original — meaning, tone and every fact.", tag: "Stage 2" },
  { icon: BookOpenCheck, title: "Valid EPUB output", body: "Chapters, table of contents, metadata and cover — compiled into KDP-ready files. Zero manual conversion.", tag: "Compile" },
  { icon: FileDown, title: "Whole catalog", body: "Ten languages, ten EPUBs. Download each one — or the full catalog as a single archive.", tag: "Ship" },
];

export default function Landing() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#f6f5f1]">
      <Header />

      {/* Hero */}
      <section className="relative overflow-hidden grain">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-5 pb-20 pt-16 sm:px-8 lg:grid-cols-12 lg:pt-24">
          <div className="lg:col-span-7">
            <motion.span
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 rounded-full border border-[#e6e4dd] bg-white px-3 py-1 text-xs font-medium text-[#475569]">
              <Sparkles className="h-3.5 w-3.5 text-[#d97706]" /> One manuscript → an entire global catalog
            </motion.span>

            <motion.h1
              initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.05 }}
              className="font-display mt-6 text-4xl leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
              Publish in ten languages,
              <span className="text-[#d97706]"> in your own voice.</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.12 }}
              className="mt-6 max-w-xl text-base leading-relaxed text-[#475569] sm:text-lg">
              AI Publishing Coach turns a single KDP manuscript into a catalog of verified EPUB files — one per language.
              RAVIRA translates. A corrector of your choice guards your meaning, tone and every fact. You ship with certainty.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.18 }}
              className="mt-9 flex flex-wrap items-center gap-3">
              <Button data-testid="hero-start-btn" onClick={() => navigate("/login")}
                className="group h-12 rounded-full bg-[#0f172a] px-7 text-white hover:bg-[#1e293b]">
                Enter the studio
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Button>
              <div className="flex items-center gap-2 text-sm text-[#475569]">
                <Globe className="h-4 w-4 text-[#059669]" /> 106 languages · 20 domains · EPUB ready
              </div>
            </motion.div>

            <div className="mt-10 flex flex-wrap gap-6 border-t border-[#e6e4dd] pt-6">
              {[["106", "Languages"], ["2-stage", "Verification"], ["EPUB", "KDP-ready"]].map(([n, l]) => (
                <div key={l}>
                  <div className="font-display text-2xl">{n}</div>
                  <div className="text-xs uppercase tracking-[0.15em] text-[#94a3b8]">{l}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Visual card */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.15 }}
            className="lg:col-span-5">
            <div className="relative rounded-3xl border border-[#e6e4dd] bg-white p-6 shadow-[0_20px_60px_-30px_rgba(15,23,42,0.4)]">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs uppercase tracking-[0.15em] text-[#94a3b8]">Catalog</div>
                  <div className="font-display text-lg">The Silent Compass</div>
                </div>
                <IntentRing score={98} size={72} />
              </div>
              <div className="mt-5 space-y-2.5">
                {[
                  ["🇩🇪", "German", "ready", 99],
                  ["🇪🇸", "Spanish", "ready", 97],
                  ["🇫🇷", "French", "flagged", 84],
                  ["🇯🇵", "Japanese", "verifying", null],
                ].map(([flag, name, st, sc]) => (
                  <div key={name} className="flex items-center justify-between rounded-xl border border-[#e6e4dd] bg-[#f6f5f1] px-3.5 py-2.5">
                    <div className="flex items-center gap-2.5">
                      <span className="text-lg">{flag}</span>
                      <span className="text-sm font-medium">{name}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`text-xs font-medium ${st === "ready" ? "text-[#059669]" : st === "flagged" ? "text-[#e11d48]" : "text-[#d97706]"}`}>
                        {st === "verifying" ? "verifying…" : `${sc}%`}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-5 flex items-center justify-between rounded-xl bg-[#0f172a] px-4 py-3 text-white">
                <span className="text-sm">Download full catalog</span>
                <FileDown className="h-4 w-4" />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section className="border-t border-[#e6e4dd] bg-white py-20">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-[0.2em] text-[#d97706] font-mono2">The two-storey engine</div>
            <h2 className="font-display mt-3 text-3xl tracking-tight sm:text-4xl">
              Translate, then verify — before a single word ships.
            </h2>
          </div>
          <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4">
            {FEATURES.map((f, i) => (
              <motion.div key={f.title}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="group rounded-2xl border border-[#e6e4dd] bg-[#f6f5f1] p-6 transition-all hover:-translate-y-1 hover:shadow-lg">
                <div className="flex items-center justify-between">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#0f172a] text-white">
                    <f.icon className="h-5 w-5" />
                  </div>
                  <span className="text-[10px] uppercase tracking-[0.15em] text-[#94a3b8] font-mono2">{f.tag}</span>
                </div>
                <h3 className="mt-5 text-lg font-semibold">{f.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-[#475569]">{f.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0f172a] py-20 text-white">
        <div className="mx-auto max-w-4xl px-5 text-center sm:px-8">
          <Layers className="mx-auto h-8 w-8 text-[#d97706]" />
          <h2 className="font-display mt-5 text-3xl tracking-tight sm:text-4xl">
            From one manuscript to a global bookshelf.
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-slate-300">
            Set up your RAVIRA key and corrector model, upload your book, pick your markets. Under two minutes to a verified catalog.
          </p>
          <Button data-testid="cta-start-btn" onClick={() => navigate("/login")}
            className="mt-8 h-12 rounded-full bg-white px-8 text-[#0f172a] hover:bg-[#d97706] hover:text-white">
            Get started <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </section>

      <footer className="border-t border-[#1e293b] bg-[#0f172a] py-8 text-center text-sm text-slate-400">
        <ShieldCheck className="mx-auto mb-2 h-4 w-4" />
        AI Publishing Coach · Powered by RAVIRA API
      </footer>
    </div>
  );
}
